import React, { useCallback, useEffect, useMemo, useState, useRef, createElement, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PlusIcon, BarChart3Icon, MessageSquareIcon, SettingsIcon, PackageIcon, StarIcon, EyeIcon, PencilIcon, CopyIcon, TrashIcon, ChartBarIcon, DollarSignIcon, ShoppingCartIcon, CreditCardIcon, BellIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, InfoIcon, ExternalLinkIcon, FileTextIcon, ArrowRightIcon, BanknoteIcon, WalletIcon, CalendarIcon, ClockIcon, DownloadIcon, FilterIcon, SlidersIcon, RefreshCwIcon, SaveIcon, XIcon, CheckIcon, ChevronDownIcon, UploadIcon, ImageIcon, PlayIcon } from 'lucide-react';
import { useAuth } from '../components/auth/AuthProvider';
import { getProductsBySeller, deleteProduct, getOrdersBySeller, getPayoutsBySeller, FirestoreProduct, createProduct, updateProduct } from '../firebase/firestore';
import { getNotificationsByUser, markNotificationAsRead } from '../firebase/auth';
import { collection, doc, setDoc, getDoc, getDocs, query, where, orderBy, addDoc, updateDoc, Timestamp, onSnapshot, deleteDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db, storage, auth } from '../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig';
import { uploadFile, generateStoragePath, deleteFile } from '../firebase/storage';
import { AnalyticsChart } from '../components/dashboard/AnalyticsChart';
import { MessageCenter } from '../components/dashboard/MessageCenter';
import { DeleteConfirmationModal } from '../components/modals/DeleteConfirmationModal';
// Product type definition
interface Product {
  id: number | string;
  title: string;
  description: string;
  price: number;
  category: string;
  image: string;
  rating: number;
  sales: number;
  revenue: number;
  status: 'published' | 'draft' | 'archived' | 'pending';
  productFileUrl?: string;
}
// Backfill function to ensure all products have updatedAt timestamp
const backfillProductTimestamps = async () => {
  console.log('Starting backfill process for product timestamps...');
  try {
    // Get all products without limit
    const productsRef = collection(db, 'products');
    const productsSnapshot = await getDocs(productsRef);
    const total = productsSnapshot.size;
    let updated = 0;
    let errors = 0;
    console.log(`Found ${total} products to check for updatedAt field`);
    // Process each product
    const updatePromises = productsSnapshot.docs.map(async docSnapshot => {
      const productId = docSnapshot.id;
      const productData = docSnapshot.data();
      try {
        // Check if updatedAt is missing or needs to be converted
        let needsUpdate = false;
        if (!productData.updatedAt) {
          needsUpdate = true;
          console.log(`Product ${productId} is missing updatedAt field`);
        } else if (!(productData.updatedAt instanceof Timestamp) && typeof productData.updatedAt !== 'object') {
          needsUpdate = true;
          console.log(`Product ${productId} has invalid updatedAt field type: ${typeof productData.updatedAt}`);
        }
        if (needsUpdate) {
          // Use createdAt as fallback, or current timestamp if createdAt is also missing
          const timestamp = productData.createdAt || serverTimestamp();
          await updateDoc(doc(db, 'products', productId), {
            updatedAt: timestamp
          });
          console.log(`Updated product ${productId} with new updatedAt timestamp`);
          updated++;
        }
      } catch (err) {
        console.error(`Error updating product ${productId}:`, err);
        errors++;
      }
    });
    // Wait for all update operations to complete
    await Promise.all(updatePromises);
    console.log(`Backfill complete. Updated ${updated} products. Errors: ${errors}`);
    return {
      total,
      updated,
      errors
    };
  } catch (error) {
    console.error('Error in backfill process:', error);
    throw error;
  }
};
// Add this function inside the SellerDashboard component
const runBackfillTimestamps = async () => {
  try {
    setLoading(prev => ({
      ...prev,
      products: true
    }));
    setUpdateError(null);
    console.log('Starting backfill process...');
    const result = await backfillProductTimestamps();
    setUpdateSuccess(`Backfill complete. Updated ${result.updated} products out of ${result.total} total. Errors: ${result.errors}`);
    // Force refresh the products list
    setActiveTab(activeTab === 'products' ? 'products_refresh' : 'products');
    setTimeout(() => {
      setUpdateSuccess(null);
    }, 5000);
  } catch (error) {
    console.error('Error running backfill:', error);
    setUpdateError('Failed to backfill product timestamps. Please try again.');
    setTimeout(() => {
      setUpdateError(null);
    }, 5000);
  } finally {
    setLoading(prev => ({
      ...prev,
      products: false
    }));
  }
};
// Editable product component
const EditableProductCard = ({
  product,
  onSave,
  onCancel,
  isEditing,
  userId
}: {
  product: Product;
  onSave: (updatedProduct: Product) => void;
  onCancel: () => void;
  isEditing: boolean;
  userId?: string;
}) => {
  const [formData, setFormData] = useState({
    title: product.title,
    description: product.description,
    price: product.price.toString(),
    category: product.category,
    status: product.status,
    image: product.image
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  // Category options
  const categoryOptions = ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Prompt Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates', 'eBooks & PDF Guides', 'Course Slides & Templates', 'Printable Planners', 'Email Templates & Flows', 'Copywriting Packs', 'Fonts & Typefaces', 'Icon Packs', '3D Assets & Mockups', 'Canva Templates', 'Social Media Content Packs', 'Legal Templates', 'Resume & Portfolio Templates', 'Other'];
  // Status options
  const statusOptions = [{
    value: 'published',
    label: 'Published'
  }, {
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'archived',
    label: 'Archived'
  }];
  useEffect(() => {
    // Reset form data when product changes
    setFormData({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      status: product.status,
      image: product.image
    });
    setErrors({});
    setIsUploadingMedia(false);
    setUploadProgress(0);
  }, [product, isEditing]);
  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !userId) return;
    const file = e.target.files[0];
    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        media: 'File size exceeds 50MB limit'
      }));
      return;
    }
    setIsUploadingMedia(true);
    setUploadProgress(0);
    setErrors(prev => {
      const {
        media,
        ...rest
      } = prev;
      return rest;
    });
    const fileType = file.type.startsWith('video/') ? 'video' as const : file.type.startsWith('image/') ? 'thumbnail' as const : 'product' as const;
    const path = generateStoragePath(userId, fileType, file.name);
    uploadFile(file, path, progress => {
      setUploadProgress(progress);
    }, error => {
      setIsUploadingMedia(false);
      setUploadProgress(0);
      setErrors(prev => ({
        ...prev,
        media: `Upload failed: ${error.message}`
      }));
    }, downloadUrl => {
      setIsUploadingMedia(false);
      setUploadProgress(100);
      setFormData(prev => ({
        ...prev,
        image: downloadUrl
      }));
    });
    // Reset file input
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };
  const handleRemoveMedia = () => {
    setFormData(prev => ({
      ...prev,
      image: ''
    }));
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.price.trim()) {
      newErrors.price = 'Price is required';
    } else {
      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue < 0) {
        newErrors.price = 'Please enter a valid price';
      }
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    return newErrors;
  };
  const handleSubmit = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Create updated product object
    const updatedProduct = {
      ...product,
      title: formData.title,
      description: formData.description,
      price: parseFloat(formData.price),
      category: formData.category,
      status: formData.status as 'published' | 'draft' | 'archived' | 'pending',
      image: formData.image
    };
    onSave(updatedProduct);
  };
  return <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" data-id="element-5534">
      <div className="relative aspect-[16/9] overflow-hidden" data-id="element-5535">
        {formData.image ? <img src={formData.image} alt={product.title} className="w-full h-full object-cover" data-id="element-5536" /> : <div className="w-full h-full bg-gray-100 dark:bg-[#1A3330] flex flex-col items-center justify-center" data-id="element-5537">
            <ImageIcon className="h-10 w-10 text-gray-300 dark:text-[#7A8F8A] mb-2" data-id="element-5538" />
            <span className="text-xs text-gray-400 dark:text-[#7A8F8A]" data-id="element-5539">
              No media
            </span>
          </div>}
        <div className="absolute top-3 left-3" data-id="element-5540">
          <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-5541">
            {product.category}
          </span>
        </div>
        {product.status !== 'published' && <div className="absolute bottom-3 left-3" data-id="element-5542">
            <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${product.status === 'draft' ? 'bg-gray-500' : product.status === 'archived' ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-5543">
              {product.status.toUpperCase()}
            </span>
          </div>}
      </div>
      <div className="p-5" data-id="element-5544">
        {isEditing ? <div className="space-y-4" data-id="element-5545">
            {/* Media Section — thumbnail update only */}
            <div data-id="element-5546">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-5547">
                Thumbnail
              </label>
              <input type="file" ref={mediaInputRef} onChange={handleMediaUpload} className="hidden" accept="image/*" data-id="element-5548" />
              {formData.image && <div className="mb-2 w-20 h-14 rounded overflow-hidden border border-gray-200 dark:border-[#1A3330]" data-id="element-5549">
                  <img src={formData.image} alt="Current thumbnail" className="w-full h-full object-cover" data-id="element-5550" />
                </div>}
              {isUploadingMedia ? <div className="border border-gray-200 dark:border-[#1A3330] rounded-lg p-3" data-id="element-5551">
                  <div className="flex items-center justify-between mb-1" data-id="element-5552">
                    <span className="text-xs font-medium text-gray-600 dark:text-[#B0C1BD]" data-id="element-5553">
                      {uploadProgress < 100 ? 'Uploading...' : 'Processing...'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5554">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-[#1A3330] rounded-full h-1.5 overflow-hidden" data-id="element-5555">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] h-1.5 rounded-full transition-all duration-300" style={{
                width: `${uploadProgress}%`
              }} data-id="element-5556"></div>
                  </div>
                </div> : <button type="button" onClick={() => mediaInputRef.current?.click()} className="flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md text-sm text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-5557">
                  <ImageIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-5558" />
                  {formData.image ? 'Change Thumbnail' : 'Upload Thumbnail'}
                </button>}
              {errors.media && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5559">
                  {errors.media}
                </p>}
            </div>
            <div data-id="element-5560">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-5561">
                Title
              </label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.title ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-5562" />
              {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5563">
                  {errors.title}
                </p>}
            </div>
            <div data-id="element-5564">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-5565">
                Full Description
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD] resize-y" placeholder="Describe your product..." data-id="element-5566" />
            </div>
            <div data-id="element-5567">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-5568">
                Price (USD)
              </label>
              <div className="relative" data-id="element-5569">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-[#7A8F8A]" data-id="element-5570">
                  $
                </span>
                <input type="text" name="price" value={formData.price} onChange={handleChange} className={`w-full pl-8 pr-4 py-2 border ${errors.price ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-5571" />
              </div>
              {errors.price && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5572">
                  {errors.price}
                </p>}
            </div>
            <div data-id="element-5573">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-5574">
                Category
              </label>
              <select name="category" value={formData.category} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.category ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-5575">
                <option value="" data-id="element-5576">Select Category</option>
                {categoryOptions.map(category => <option key={category} value={category} data-id="element-5577">
                    {category}
                  </option>)}
              </select>
              {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5578">
                  {errors.category}
                </p>}
            </div>
            <div data-id="element-5579">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-5580">
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.status ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-5581">
                {statusOptions.map(option => <option key={option.value} value={option.value} data-id="element-5582">
                    {option.label}
                  </option>)}
              </select>
              {errors.status && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5583">
                  {errors.status}
                </p>}
            </div>
            <div className="flex space-x-2 pt-2" data-id="element-5584">
              <button onClick={handleSubmit} className="flex-1 bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center" data-id="element-5585">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-5586" />
                Save
              </button>
              <button onClick={onCancel} className="flex-1 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-5587">
                <XIcon className="h-4 w-4 mr-2" data-id="element-5588" />
                Cancel
              </button>
            </div>
          </div> : <>
            <h3 className="text-lg font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9]" data-id="element-5589">
              {product.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-3 line-clamp-2" data-id="element-5590">
              {product.description}
            </p>
            <div className="flex items-center justify-between mb-3" data-id="element-5591">
              <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5592">
                ${product.price}
              </span>
              <div className="flex items-center" data-id="element-5593">
                {product.rating > 0 ? <>
                    <StarIcon className="h-4 w-4 text-yellow-400 fill-current" data-id="element-5594" />
                    <span className="text-sm text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-5595">
                      {product.rating}
                    </span>
                  </> : <span className="text-sm text-gray-400 dark:text-[#7A8F8A]" data-id="element-5596">
                    No ratings yet
                  </span>}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-5597">
              <span data-id="element-5598">Sales: {product.sales}</span>
              <span data-id="element-5599">Revenue: ${product.revenue}</span>
            </div>
          </>}
      </div>
    </div>;
};
// Product card component for seller dashboard
const ProductCard = ({
  product,
  onPreview,
  onEdit,
  onDuplicate,
  onDelete,
  onViewAnalytics,
  onArchive,
  isEditing,
  editingProductId,
  onSaveEdit,
  onCancelEdit,
  userId
}: {
  product: Product;
  onPreview: (id: number | string) => void;
  onEdit: (id: number | string) => void;
  onDuplicate: (id: number | string) => void;
  onDelete: (id: number | string) => void;
  onViewAnalytics: (id: number | string) => void;
  onArchive: (id: number | string) => void;
  isEditing: boolean;
  editingProductId: string | number | null;
  onSaveEdit: (updatedProduct: Product) => void;
  onCancelEdit: () => void;
  userId?: string;
}) => {
  const navigate = useNavigate();
  const isEditingThisProduct = isEditing && editingProductId === product.id;
  const [imageLoaded, setImageLoaded] = useState(false);
  const isVideo = product.category === 'Short-form Video' || product.category === 'Landscape Video';
  // Preload the image
  useEffect(() => {
    if (product.image) {
      const img = new Image();
      img.src = product.image;
      img.onload = () => setImageLoaded(true);
    }
  }, [product.image]);
  if (isEditingThisProduct) {
    return <EditableProductCard product={product} onSave={onSaveEdit} onCancel={onCancelEdit} isEditing={true} userId={userId} data-id="element-5600" />;
  }
  return <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" data-id="element-5601">
      <div className="relative aspect-[16/9] overflow-hidden" data-id="element-5602">
        {/* Show a placeholder while image is loading */}
        {!imageLoaded && <div className="absolute inset-0 bg-gray-200 dark:bg-[#1A3330] flex items-center justify-center" data-id="element-5603">
            <div className="animate-pulse w-full h-full" data-id="element-5604"></div>
          </div>}
        <img src={product.image} alt={product.title} className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setImageLoaded(true)} data-id="element-5605" />
        {/* Video play button overlay — own click handler with stopPropagation */}
        {isVideo && <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={e => {
        e.stopPropagation();
        navigate(`/product/${product.id}`);
      }} data-id="element-5606">
            <div className="w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 transition-colors flex items-center justify-center" data-id="element-5607">
              <PlayIcon className="h-6 w-6 text-white" data-id="element-5608" />
            </div>
          </div>}
        <div className="absolute top-3 left-3" data-id="element-5609">
          <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-5610">
            {product.category}
          </span>
        </div>
        {product.status !== 'published' && <div className="absolute bottom-3 left-3" data-id="element-5611">
            <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${product.status === 'draft' ? 'bg-gray-500' : product.status === 'archived' ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-5612">
              {product.status.toUpperCase()}
            </span>
          </div>}
      </div>
      <div className="p-5" data-id="element-5613">
        <h3 className="text-lg font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9]" data-id="element-5614">
          {product.title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-3 line-clamp-2" data-id="element-5615">
          {product.description}
        </p>
        <div className="flex items-center justify-between mb-3" data-id="element-5616">
          <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5617">
            ${product.price}
          </span>
          <div className="flex items-center" data-id="element-5618">
            {product.rating > 0 ? <>
                <StarIcon className="h-4 w-4 text-yellow-400 fill-current" data-id="element-5619" />
                <span className="text-sm text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-5620">
                  {product.rating}
                </span>
              </> : <span className="text-sm text-gray-400 dark:text-[#7A8F8A]" data-id="element-5621">
                No ratings yet
              </span>}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-5622">
          <span data-id="element-5623">Sales: {product.sales}</span>
          <span data-id="element-5624">Revenue: ${product.revenue}</span>
        </div>
        {/* File attachment indicator */}
        {product.productFileUrl && <div className="flex items-center justify-between mb-3 px-2.5 py-2 bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800/30 rounded-lg" data-id="element-5625">
            <span className="flex items-center text-xs font-medium text-green-700 dark:text-green-400" data-id="element-5626">
              <FileTextIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-5627" />
              File attached
            </span>
            <button onClick={async () => {
          try {
            const response = await fetch(product.productFileUrl!);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = product.title || 'download';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error('Download failed:', error);
          }
        }} className="flex items-center text-xs font-medium text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors" data-id="element-5628">
              <DownloadIcon className="h-3.5 w-3.5 mr-1" data-id="element-5629" />
              Download
            </button>
          </div>}
        <div className="grid grid-cols-2 gap-2 mb-2" data-id="element-5630">
          <button onClick={() => onPreview(product.id)} className="bg-[#0A3C52] dark:bg-[#0B3C45] text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center hover:opacity-90 transition-opacity" data-id="element-5631">
            <EyeIcon className="h-3.5 w-3.5 mr-1" data-id="element-5632" />
            View as Buyer
          </button>
          <button onClick={() => onEdit(product.id)} className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center" data-id="element-5633">
            <PencilIcon className="h-3.5 w-3.5 mr-1" data-id="element-5634" />
            Edit
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2" data-id="element-5635">
          <button onClick={() => onViewAnalytics(product.id)} className="bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-5636">
            <ChartBarIcon className="h-3 w-3 mr-1" data-id="element-5637" />
            Analytics
          </button>
          <button onClick={() => onDuplicate(product.id)} className="bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-5638">
            <CopyIcon className="h-3 w-3 mr-1" data-id="element-5639" />
            Duplicate
          </button>
          {product.status === 'archived' ? <button onClick={() => onDelete(product.id)} className="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center col-span-2" data-id="element-5640">
              <TrashIcon className="h-3 w-3 mr-1" data-id="element-5641" />
              Delete
            </button> : <>
              <button onClick={() => onArchive(product.id)} className="bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-5642">
                <XIcon className="h-3 w-3 mr-1" data-id="element-5643" />
                Archive
              </button>
              <button onClick={() => onDelete(product.id)} className="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center" data-id="element-5644">
                <TrashIcon className="h-3 w-3 mr-1" data-id="element-5645" />
                Delete
              </button>
            </>}
        </div>
      </div>
    </div>;
};
export const SellerDashboard = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const {
    isAuthenticated,
    user
  } = useAuth();
  const navigate = useNavigate();
  // Add missing constant declaration
  const [productsUnsubscribe, setProductsUnsubscribe] = useState<(() => void) | null>(null);
  // States for data
  const [sellerProducts, setSellerProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState({
    products: true,
    orders: true,
    payouts: true,
    notifications: true
  });
  // States for filters and sorting
  const [productFilter, setProductFilter] = useState('all');
  const [productSort, setProductSort] = useState('newest');
  const [orderFilter, setOrderFilter] = useState('all');
  const [orderSort, setOrderSort] = useState('newest');
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  // States for modals
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  // States for notification filters
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // States for payment method
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [lastPayoutDate, setLastPayoutDate] = useState('');
  const [lastPayoutAmount, setLastPayoutAmount] = useState(0);
  // States for product editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | number | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  // New state for products error handling
  const [productsError, setProductsError] = useState<string | null>(null);
  const [queryAttempt, setQueryAttempt] = useState(0); // Track query attempts
  // Add new state for retry timeout management
  const [retryTimeout, setRetryTimeout] = useState<NodeJS.Timeout | null>(null);
  // Add new state to track if component is mounted
  const [isMounted, setIsMounted] = useState(true);
  // Add a constant for maximum retries
  const MAX_RETRY_ATTEMPTS = 2;
  const hasAffiliateRole = user?.roles?.includes('affiliate') || false;
  // State for profile image and banner from Firestore
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileBanner, setProfileBanner] = useState<string | null>(null);
  // Fetch profile image and banner from Firestore
  useEffect(() => {
    if (!user?.id) return;
    const fetchProfileData = async () => {
      try {
        const userDocRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          const image = data.profileImage || data.avatar || data.avatarUrl || data.photoURL || null;
          setProfileImage(image);
          const banner = data.profileBanner || data.storeBanner || data.coverImage || null;
          setProfileBanner(banner);
        }
      } catch (err) {
        console.warn('Failed to fetch profile data:', err);
      }
    };
    fetchProfileData();
  }, [user?.id]);
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    // Track last visited dashboard for login redirect
    localStorage.setItem('dgstack_last_dashboard', 'seller');
    // Mark component as mounted
    setIsMounted(true);
    // Reset error and loading states when component mounts or activeTab changes
    setProductsError(null);
    setLoading(prev => ({
      ...prev,
      products: true
    }));
    // Function to fetch products with primary query (using updatedAt)
    const fetchWithPrimaryQuery = sellerId => {
      try {
        // Clean up previous listener if it exists
        if (productsUnsubscribe) {
          productsUnsubscribe();
        }
        // Primary query using updatedAt
        const primaryQuery = query(collection(db, 'products'), where('sellerId', '==', sellerId), orderBy('updatedAt', 'desc'), limit(50));
        const unsubscribe = onSnapshot(primaryQuery, snapshot => {
          if (!isMounted) return;
          const productsData = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            // Calculate sales and revenue from orders
            let sales = 0;
            let revenue = 0;
            orders.forEach(order => {
              if (order.productId === doc.id && order.status === 'paid') {
                sales++;
                revenue += order.amount;
              }
            });
            productsData.push({
              id: doc.id,
              title: data.title || '',
              description: data.shortDescription || data.description || '',
              price: data.price || 0,
              category: data.category || 'Other',
              image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
              rating: data.rating || 0,
              sales: sales,
              revenue: revenue,
              status: data.status || 'published',
              productFileUrl: data.productFileUrl || data.fileUrl || ''
            });
          });
          // Update state with products data
          setSellerProducts(productsData);
          setLoading(prev => ({
            ...prev,
            products: false
          }));
          setProductsError(null);
          setQueryAttempt(0);
        }, error => {
          if (!isMounted) return;
          console.error('Primary query error:', error);
          // Check if the error is related to missing indexes
          if (error.toString().includes('index')) {
            const indexMatch = error.toString().match(/(https:\/\/console\.firebase\.google\.com\/[^\s]+)/);
            if (indexMatch) {
              console.error('Missing index. Create it here:', indexMatch[0]);
              setProductsError(`Missing Firestore index. Please create the index at: ${indexMatch[0]}`);
            } else {
              setProductsError('Failed to load your products due to a missing index. Please try again later.');
            }
          } else {
            setProductsError('Failed to load your products. Please try again.');
          }
          // Try fallback query with createdAt, but only if we haven't exceeded max retries
          if (queryAttempt < MAX_RETRY_ATTEMPTS) {
            const newAttemptCount = queryAttempt + 1;
            setQueryAttempt(newAttemptCount);
            tryFallbackQuery(sellerId);
          } else {
            trySimpleQuery(sellerId);
          }
        });
        // Update the unsubscribe function
        setProductsUnsubscribe(() => unsubscribe);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up primary query:', error);
        if (queryAttempt < MAX_RETRY_ATTEMPTS) {
          const newAttemptCount = queryAttempt + 1;
          setQueryAttempt(newAttemptCount);
          tryFallbackQuery(sellerId);
        } else {
          trySimpleQuery(sellerId);
        }
      }
    };
    // Function to try a fallback query if the primary one fails
    const tryFallbackQuery = sellerId => {
      try {
        // Clean up previous listener if it exists
        if (productsUnsubscribe) {
          productsUnsubscribe();
        }
        // Fallback query using createdAt instead of updatedAt
        const fallbackQuery = query(collection(db, 'products'), where('sellerId', '==', sellerId), orderBy('createdAt', 'desc'), limit(50));
        const unsubscribe = onSnapshot(fallbackQuery, snapshot => {
          if (!isMounted) return;
          const productsData = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            // Calculate sales and revenue from orders
            let sales = 0;
            let revenue = 0;
            orders.forEach(order => {
              if (order.productId === doc.id && order.status === 'paid') {
                sales++;
                revenue += order.amount;
              }
            });
            productsData.push({
              id: doc.id,
              title: data.title || '',
              description: data.shortDescription || data.description || '',
              price: data.price || 0,
              category: data.category || 'Other',
              image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
              rating: data.rating || 0,
              sales: sales,
              revenue: revenue,
              status: data.status || 'published',
              productFileUrl: data.productFileUrl || data.fileUrl || ''
            });
          });
          setSellerProducts(productsData);
          setLoading(prev => ({
            ...prev,
            products: false
          }));
          setProductsError(null);
          setQueryAttempt(0);
        }, error => {
          if (!isMounted) return;
          console.error('Fallback query error:', error);
          trySimpleQuery(sellerId);
        });
        // Update the unsubscribe function
        setProductsUnsubscribe(() => unsubscribe);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up fallback query:', error);
        trySimpleQuery(sellerId);
      }
    };
    // Final fallback with just the sellerId filter, no sorting
    const trySimpleQuery = sellerId => {
      try {
        // Clean up previous listener if it exists
        if (productsUnsubscribe) {
          productsUnsubscribe();
        }
        // Simple query with just the sellerId filter
        const simpleQuery = query(collection(db, 'products'), where('sellerId', '==', sellerId), limit(50));
        const unsubscribe = onSnapshot(simpleQuery, snapshot => {
          if (!isMounted) return;
          const productsData = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            // Calculate sales and revenue from orders
            let sales = 0;
            let revenue = 0;
            orders.forEach(order => {
              if (order.productId === doc.id && order.status === 'paid') {
                sales++;
                revenue += order.amount;
              }
            });
            productsData.push({
              id: doc.id,
              title: data.title || '',
              description: data.shortDescription || data.description || '',
              price: data.price || 0,
              category: data.category || 'Other',
              image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
              rating: data.rating || 0,
              sales: sales,
              revenue: revenue,
              status: data.status || 'published',
              productFileUrl: data.productFileUrl || data.fileUrl || ''
            });
          });
          // Always update products data regardless of warning condition
          setSellerProducts(productsData);
          setLoading(prev => ({
            ...prev,
            products: false
          }));
          // Clear error state regardless of whether we have products or not
          setProductsError(null);
          // Only show backfill message if we have products with missing timestamps
          if (productsData.length > 0) {
            setUpdateSuccess('Products loaded successfully. For better sorting, you may run the backfill function.');
            if (user?.roles?.includes('admin')) {
              runBackfillTimestamps();
            }
          }
        }, async error => {
          if (!isMounted) return;
          console.error('Simple query error:', error);
          // Final fallback: try a one-time getDocs call
          try {
            const fallbackSnapshot = await getDocs(query(collection(db, 'products'), where('sellerId', '==', sellerId)));
            if (!isMounted) return;
            if (fallbackSnapshot.empty) {
              // No products exist — show empty state, not an error
              setSellerProducts([]);
              setProductsError(null);
            } else {
              // Products exist but realtime listener failed
              const productsData = [];
              fallbackSnapshot.forEach(doc => {
                const data = doc.data();
                productsData.push({
                  id: doc.id,
                  title: data.title || '',
                  description: data.shortDescription || data.description || '',
                  price: data.price || 0,
                  category: data.category || 'Other',
                  image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
                  rating: data.rating || 0,
                  sales: 0,
                  revenue: 0,
                  status: data.status || 'published',
                  productFileUrl: data.productFileUrl || data.fileUrl || ''
                });
              });
              setSellerProducts(productsData);
              setProductsError(null);
            }
          } catch (fallbackError) {
            if (!isMounted) return;
            console.error('Final getDocs fallback also failed:', fallbackError);
            // Even the simplest query failed — assume empty for new sellers
            setSellerProducts([]);
            setProductsError(null);
          }
          setLoading(prev => ({
            ...prev,
            products: false
          }));
        });
        // Update the unsubscribe function
        setProductsUnsubscribe(() => unsubscribe);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up simple query:', error);
        // Gracefully show empty state instead of error for new sellers
        setSellerProducts([]);
        setProductsError(null);
        setLoading(prev => ({
          ...prev,
          products: false
        }));
      }
    };
    // Start the query chain with the primary query if we have a valid user
    if (user?.id && activeTab === 'products' || activeTab === 'products_refresh') {
      fetchWithPrimaryQuery(user.id);
    }
    // Return cleanup function for all listeners
    return () => {
      setIsMounted(false);
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (productsUnsubscribe) {
        productsUnsubscribe();
        setProductsUnsubscribe(null);
      }
    };
  }, [isAuthenticated, user, navigate, activeTab]);
  // Apply filters and sorting to products
  const filteredProducts = useMemo(() => {
    let filtered = [...sellerProducts];
    // Apply filters
    if (productFilter !== 'all') {
      filtered = filtered.filter(product => product.status === productFilter);
    }
    // Apply sorting
    switch (productSort) {
      case 'newest':
        // Assuming products are already sorted by creation date
        break;
      case 'oldest':
        filtered = filtered.reverse();
        break;
      case 'price-high':
        filtered = filtered.sort((a, b) => b.price - a.price);
        break;
      case 'price-low':
        filtered = filtered.sort((a, b) => a.price - b.price);
        break;
      case 'sales':
        filtered = filtered.sort((a, b) => b.sales - a.sales);
        break;
    }
    return filtered;
  }, [sellerProducts, productFilter, productSort]);
  // Apply filters and sorting to orders
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];
    // Apply filters
    if (orderFilter !== 'all') {
      filtered = filtered.filter(order => order.status === orderFilter);
    }
    // Apply sorting
    switch (orderSort) {
      case 'newest':
        // Assuming orders are already sorted by date
        break;
      case 'oldest':
        filtered = filtered.reverse();
        break;
      case 'amount-high':
        filtered = filtered.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount-low':
        filtered = filtered.sort((a, b) => a.amount - b.amount);
        break;
    }
    return filtered;
  }, [orders, orderFilter, orderSort]);
  // Pagination logic for orders
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  // Filter notifications based on type and search query
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      const matchesFilter = notificationFilter === 'all' || notificationFilter === 'system' && (notification.type === 'approval' || notification.type === 'rejection') || notificationFilter === 'messages' && notification.type === 'message' || notificationFilter === 'payouts' && notification.type === 'payout' || notificationFilter === 'orders' && notification.type === 'order';
      const matchesSearch = notification.message.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [notifications, notificationFilter, searchQuery]);
  const handlePreview = (productId: number | string) => {
    navigate(`/product/${productId}`);
  };
  const handleEdit = (productId: number | string) => {
    setEditingProductId(productId);
    setIsEditing(true);
    setUpdateError(null);
    setUpdateSuccess(null);
  };
  const handleCancelEdit = () => {
    setEditingProductId(null);
    setIsEditing(false);
    setUpdateError(null);
  };
  const handleSaveEdit = async (updatedProduct: Product) => {
    try {
      setUpdateError(null);
      setUpdateSuccess(null);
      // Update product in Firestore
      const productRef = doc(db, 'products', updatedProduct.id.toString());
      // Prepare update data
      const updateData: Record<string, any> = {
        title: updatedProduct.title,
        description: updatedProduct.description,
        price: updatedProduct.price,
        category: updatedProduct.category,
        status: updatedProduct.status,
        image: updatedProduct.image,
        updatedAt: serverTimestamp()
      };
      await updateDoc(productRef, updateData);
      // Success message
      setUpdateSuccess('Product updated successfully!');
      // Reset editing state
      setIsEditing(false);
      setEditingProductId(null);
      // Clear success message after a delay
      setTimeout(() => {
        setUpdateSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating product:', error);
      setUpdateError('Failed to update product. Please try again.');
    }
  };
  const handleDuplicate = async (productId: number | string) => {
    if (!user?.id) return;
    try {
      // Get the original product
      const productRef = doc(db, 'products', productId.toString());
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }
      const productData = productSnap.data();
      // Create a duplicate with "(Copy)" in the title
      const newProduct = {
        ...productData,
        title: `${productData.title} (Copy)`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft' // Set as draft by default
      };
      // Add to Firestore
      await addDoc(collection(db, 'products'), newProduct);
      // Show success message
      setUpdateSuccess('Product duplicated successfully. The copy is in your drafts.');
      // Clear success message after a delay
      setTimeout(() => {
        setUpdateSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error duplicating product:', error);
      setUpdateError('Error duplicating product. Please try again.');
      // Clear error message after a delay
      setTimeout(() => {
        setUpdateError(null);
      }, 3000);
    }
  };
  const handleArchive = async (productId: number | string) => {
    if (confirm('Are you sure you want to archive this product? It will no longer be visible to buyers.')) {
      try {
        // Update product status to archived
        const productRef = doc(db, 'products', productId.toString());
        await updateDoc(productRef, {
          status: 'archived',
          updatedAt: serverTimestamp()
        });
        // Show success message
        setUpdateSuccess('Product archived successfully!');
        // Clear success message after a delay
        setTimeout(() => {
          setUpdateSuccess(null);
        }, 3000);
      } catch (error) {
        console.error('Error archiving product:', error);
        setUpdateError('Failed to archive product. Please try again.');
        // Clear error message after a delay
        setTimeout(() => {
          setUpdateError(null);
        }, 3000);
      }
    }
  };
  // Add new state for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async (productId: number | string) => {
    setProductToDelete(productId);
    setIsDeleteModalOpen(true);
  };
  // Update the confirmDelete function to include a direct Firestore deletion fallback
  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      setIsDeleting(true);
      setUpdateError(null);
      setUpdateSuccess(null);
      // First, try the callable function approach
      try {
        const deleteProductCompletely = httpsCallable(functions, 'deleteProductCompletelyCallable');
        const result = await deleteProductCompletely({
          productId: productToDelete
        });
        const data = (result as any).data as {
          success: boolean;
          message?: string;
          summary?: {
            filesDeleted?: number;
            purchasesUpdated?: number;
          };
        };
        if (!data || data.success !== true) {
          throw new Error(data?.message || 'Failed to delete product');
        }
        // Success path
        setSellerProducts(prev => prev.filter(p => p.id !== productToDelete));
        let msg = 'Product deleted successfully!';
        if (data.summary) {
          const fd = typeof data.summary.filesDeleted === 'number' ? data.summary.filesDeleted : 0;
          const pu = typeof data.summary.purchasesUpdated === 'number' ? data.summary.purchasesUpdated : 0;
          msg += ` (${fd} files deleted, ${pu} purchases updated)`;
        }
        setUpdateSuccess(msg);
        setIsDeleteModalOpen(false);
        setProductToDelete(null);
        setTimeout(() => setUpdateSuccess(null), 5000);
        return; // Exit early on success
      } catch (callableError) {
        console.error('Callable function error:', callableError);
        // Fall through to HTTP approach if callable fails
      }
      // Second, try the HTTP function approach
      try {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) {
          throw new Error('Authentication required');
        }
        // Replace [YOUR-PROJECT-ID] with your actual Firebase project ID
        const response = await fetch('https://us-central1-[YOUR-PROJECT-ID].cloudfunctions.net/deleteProductCompletelyHttp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            productId: productToDelete
          })
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete product');
        }
        const data = await response.json();
        if (!data || data.success !== true) {
          throw new Error(data?.message || 'Failed to delete product');
        }
        // Success path for HTTP approach
        setSellerProducts(prev => prev.filter(p => p.id !== productToDelete));
        let msg = 'Product deleted successfully via HTTP function.';
        if (data.summary) {
          const fd = typeof data.summary.filesDeleted === 'number' ? data.summary.filesDeleted : 0;
          const pu = typeof data.summary.purchasesUpdated === 'number' ? data.summary.purchasesUpdated : 0;
          msg += ` (${fd} files deleted, ${pu} purchases updated)`;
        }
        setUpdateSuccess(msg);
        setIsDeleteModalOpen(false);
        setProductToDelete(null);
        setTimeout(() => setUpdateSuccess(null), 5000);
        return; // Exit early on success
      } catch (httpError) {
        console.error('HTTP function error:', httpError);
        // Fall through to direct Firestore deletion as last resort
      }
      // Last resort: Direct Firestore deletion
      console.log('Attempting direct Firestore deletion as last resort');
      // Delete the product document directly
      await deleteDoc(doc(db, 'products', productToDelete.toString()));
      // Update local state
      setSellerProducts(prev => prev.filter(p => p.id !== productToDelete));
      // Show success message
      setUpdateSuccess('Product deleted successfully via direct Firestore operation.');
      setIsDeleteModalOpen(false);
      setProductToDelete(null);
      setTimeout(() => setUpdateSuccess(null), 5000);
    } catch (e: any) {
      console.error('Delete error:', e);
      let m = 'Failed to delete product. Please try again.';
      const t = String(e?.message || e).toLowerCase();
      if (t.includes('unauthenticated') || t.includes('token')) {
        m = 'You must be logged in to delete a product. Please refresh and try again.';
      } else if (t.includes('permission')) {
        m = 'You do not have permission to delete this product.';
      } else if (t.includes('not found')) {
        m = "The product you're trying to delete could not be found.";
      } else if (t.includes('network') || t.includes('connection')) {
        m = 'Network error. Please check your connection and try again.';
      } else if (t.includes('internal') || t.includes('server')) {
        m = 'Server error. Please try again later.';
      }
      setUpdateError(m);
      setTimeout(() => setUpdateError(null), 5000);
    } finally {
      setIsDeleting(false);
    }
  };
  // Function to cancel deletion
  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setProductToDelete(null);
  };
  const handleViewAnalytics = (productId: number | string) => {
    // Set active tab to analytics and store the product ID
    setActiveTab('analytics');
    // You could store the selected product ID in state if needed
  };
  const handleAddNewProduct = () => {
    navigate('/sell-product');
  };
  // Function to navigate to seller's public profile
  const navigateToPublicProfile = () => {
    // Use the current user's ID if it exists in the sample data, otherwise fallback to a default
    const profileId = user?.id || 'designmaster';
    navigate(`/seller/${profileId}`);
  };
  // Function to view order details
  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };
  // Function to handle notification action
  const handleNotificationAction = async (notification: Notification) => {
    try {
      // Mark notification as read
      await markNotificationAsRead(notification.id);
      // Update UI (this will happen automatically via the real-time listener)
      // Handle action
      if (notification.cta) {
        if (notification.cta.action === 'view-product' && notification.cta.target) {
          // Navigate to product page
          navigate(`/product/${notification.cta.target}`);
        } else if (notification.cta.action === 'view-payout') {
          // Set active tab to payouts
          setActiveTab('payouts');
        } else if (notification.cta.action === 'reply' && notification.cta.target) {
          // Set active tab to messages with specific conversation
          setActiveTab('messages');
          // You could set a state variable to open the specific conversation
        } else if (notification.cta.action === 'fix-product' && notification.cta.target) {
          // Navigate to edit product page
          navigate(`/sell-product?edit=${notification.cta.target}`);
        } else if (notification.cta.action === 'view-order' && notification.cta.target) {
          // Set active tab to orders and find the specific order
          setActiveTab('orders');
          const order = orders.find(o => o.id === notification.cta?.target);
          if (order) {
            handleViewOrder(order);
          }
        }
      }
    } catch (error) {
      console.error('Error handling notification action:', error);
    }
  };
  // Function to handle page change
  const changePage = (page: number) => {
    setCurrentPage(page);
  };
  // Function to request a payout
  const handleRequestPayout = async (amount: number) => {
    if (!user?.id) return;
    try {
      // Create a new payout request in Firestore
      const payoutRef = collection(db, 'payouts');
      await addDoc(payoutRef, {
        sellerId: user.id,
        amount: amount,
        status: 'pending',
        paymentMethod: 'PayPal',
        createdAt: new Date(),
        transactionId: '' // Will be filled when processed
      });
      // Update available balance (this will happen automatically via the real-time listener)
      // Create a notification for the user
      const notificationRef = collection(db, 'notifications');
      await addDoc(notificationRef, {
        userId: user.id,
        type: 'payout',
        message: `Your payout request for $${amount.toFixed(2)} has been submitted and is being processed.`,
        read: false,
        createdAt: new Date(),
        cta: {
          label: 'View Payout',
          action: 'view-payout'
        }
      });
      // Show success message
      alert(`Your payout request for $${amount.toFixed(2)} has been submitted successfully.`);
    } catch (error) {
      console.error('Error requesting payout:', error);
      throw error;
    }
  };
  // Function to manually retry loading products
  const handleRetryLoadProducts = () => {
    // Reset error state
    setProductsError(null);
    // Reset query attempt counter
    setQueryAttempt(0);
    // Set loading state
    setLoading(prev => ({
      ...prev,
      products: true
    }));
    // Force refresh by toggling activeTab
    setActiveTab(activeTab === 'products' ? 'products_refresh' : 'products');
  };
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5646">
      <Navbar scrolled={scrolled} data-id="element-5647" />
      <main className="pt-24 pb-16" data-id="element-5648">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5649">
          {/* Seller Dashboard Header */}
          <div className="mb-8" data-id="element-5650">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-5651">
              <div data-id="element-5652">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5653">
                  Seller Dashboard
                </h1>
                <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5654">
                  Manage your products, track sales, and grow your business
                </p>
              </div>
            </div>
          </div>
          {/* Seller Profile */}
          <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm mb-8" data-id="element-5655">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-5656">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white text-3xl font-bold" data-id="element-5657">
                {profileImage ? <img src={profileImage} alt={user?.name || 'Profile'} className="w-full h-full object-cover" data-id="element-5658" /> : user?.name?.charAt(0) || 'S'}
              </div>
              <div className="flex-grow" data-id="element-5659">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5660">
                  {user?.name || 'Seller Name'}
                </h2>
                <p className="text-gray-500 dark:text-[#7A8F8A] mb-3" data-id="element-5661">
                  {user?.email || 'seller@example.com'}
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-5662">
                  <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-5663">
                    <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5664">
                      Total Products
                    </p>
                    <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5665">
                      {sellerProducts.length}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-5666">
                    <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5667">
                      Total Sales
                    </p>
                    <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5668">
                      {orders.filter(order => order.status === 'paid').length}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-5669">
                    <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5670">
                      Total Revenue
                    </p>
                    <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5671">
                      ${totalEarnings.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-5672">
                <button onClick={navigateToPublicProfile} className="bg-white dark:bg-[#1A3330] border border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#2DB489]/5 dark:hover:bg-[#4ED1A0]/10 transition-all duration-300 hover:scale-103 flex items-center whitespace-nowrap" data-id="element-5673">
                  <EyeIcon className="h-4 w-4 mr-2" data-id="element-5674" />
                  View Public Profile
                </button>
              </div>
            </div>
          </div>
          {/* Dashboard Tabs */}
          <div className="mb-8 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-5675">
            <nav className="flex space-x-8 overflow-x-auto" data-id="element-5676">
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'products' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('products')} data-id="element-5677">
                <PackageIcon className="h-4 w-4 inline mr-2" data-id="element-5678" />
                My Listings
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'orders' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('orders')} data-id="element-5679">
                <ShoppingCartIcon className="h-4 w-4 inline mr-2" data-id="element-5680" />
                Orders
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payouts' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('payouts')} data-id="element-5681">
                <CreditCardIcon className="h-4 w-4 inline mr-2" data-id="element-5682" />
                Payouts
              </button>

              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'analytics' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('analytics')} data-id="element-5683">
                <BarChart3Icon className="h-4 w-4 inline mr-2" data-id="element-5684" />
                Analytics
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'messages' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('messages')} data-id="element-5685">
                <MessageSquareIcon className="h-4 w-4 inline mr-2" data-id="element-5686" />
                Messages
              </button>
            </nav>
          </div>
          {/* Tab Content */}
          {activeTab === 'products' && <div data-id="element-5687">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-5688">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5689">
                  My Listings
                </h2>
                <div className="flex items-center space-x-3" data-id="element-5690">
                  <div className="relative" data-id="element-5691">
                    <select value={productSort} onChange={e => setProductSort(e.target.value)} className="appearance-none bg-white dark:bg-[#162825] border border-gray-300 dark:border-[#1A3330] rounded-lg py-2 px-4 pr-8 leading-tight focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent text-gray-700 dark:text-[#F8FAF9]" data-id="element-5692">
                      <option value="newest" data-id="element-5693">Newest First</option>
                      <option value="oldest" data-id="element-5694">Oldest First</option>
                      <option value="price-high" data-id="element-5695">Price: High to Low</option>
                      <option value="price-low" data-id="element-5696">Price: Low to High</option>
                      <option value="popular" data-id="element-5697">Most Popular</option>
                      <option value="alphabetical" data-id="element-5698">Alphabetical</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-[#F8FAF9]" data-id="element-5699">
                      <ChevronDownIcon className="h-4 w-4" data-id="element-5700" />
                    </div>
                  </div>
                  <button onClick={handleAddNewProduct} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2DB489] dark:bg-[#4ED1A0] hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] transition-colors" data-id="element-5701">
                    <PlusIcon className="h-5 w-5 mr-1" data-id="element-5702" />
                    Add New Product
                  </button>
                </div>
              </div>
              {/* Success/Error Messages */}
              {updateSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 flex items-start" data-id="element-5703">
                  <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-5704" />
                  <span data-id="element-5705">{updateSuccess}</span>
                </div>}
              {updateError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 flex items-start" data-id="element-5706">
                  <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-5707" />
                  <span data-id="element-5708">{updateError}</span>
                </div>}
              {/* Products Loading State */}
              {loading.products ? <div className="flex justify-center items-center py-20" data-id="element-5709">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-5710"></div>
                </div> : productsError ? <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm text-center" data-id="element-5711">
                  <AlertCircleIcon className="h-16 w-16 mx-auto text-red-500 dark:text-red-400 mb-4" data-id="element-5712" />
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5713">
                    Error Loading Products
                  </h3>
                  <p className="text-gray-500 dark:text-[#B0C1BD] max-w-md mx-auto mb-6" data-id="element-5714">
                    {productsError}
                  </p>
                  <button onClick={handleRetryLoadProducts} className="inline-flex items-center px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-lg hover:bg-opacity-90 transition-colors" data-id="element-5715">
                    <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-5716" />
                    Try Again
                  </button>
                </div> : filteredProducts.length > 0 ? <div data-id="element-5717">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-5718">
                    {filteredProducts.map(product => <ProductCard key={product.id} product={product} onPreview={handlePreview} onEdit={handleEdit} onDuplicate={handleDuplicate} onDelete={handleDelete} onViewAnalytics={handleViewAnalytics} onArchive={handleArchive} isEditing={isEditing} editingProductId={editingProductId} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} userId={user?.id} data-id="element-5719" />)}
                  </div>
                </div> : <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm text-center" data-id="element-5720">
                  <PackageIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-[#1A3330] mb-4" data-id="element-5721" />
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5722">
                    No Products Yet
                  </h3>
                  <p className="text-gray-500 dark:text-[#B0C1BD] max-w-md mx-auto mb-6" data-id="element-5723">
                    You haven't created any products yet. Start selling by
                    adding your first product.
                  </p>
                  <button onClick={handleAddNewProduct} className="inline-flex items-center px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-lg hover:bg-opacity-90 transition-colors" data-id="element-5724">
                    <PlusIcon className="h-5 w-5 mr-2" data-id="element-5725" />
                    Add New Product
                  </button>
                </div>}
            </div>}
          {activeTab === 'orders' && <div className="space-y-6" data-id="element-5726">
              <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] overflow-hidden" data-id="element-5727">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1A3330] flex justify-between items-center" data-id="element-5728">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5729">
                    Recent Orders
                  </h3>
                  <button className="text-sm text-[#2DB489] dark:text-[#4ED1A0] font-medium hover:underline" data-id="element-5730">
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto" data-id="element-5731">
                  <table className="w-full text-left border-collapse" data-id="element-5732">
                    <thead data-id="element-5733">
                      <tr className="bg-gray-50 dark:bg-[#0F1F1D] border-b border-gray-100 dark:border-[#1A3330]" data-id="element-5734">
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-5735">
                          Order
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-5736">
                          Product
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-5737">
                          Date
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-5738">
                          Status
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider text-right" data-id="element-5739">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-[#1A3330]" data-id="element-5740">
                      <tr className="hover:bg-gray-50 dark:hover:bg-[#1A3330]/50 transition-colors" data-id="element-5741">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-5742">
                          #ORD-7A9B2C
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5743">
                          <div className="flex items-center" data-id="element-5744">
                            <div className="h-10 w-10 rounded-md bg-gray-100 dark:bg-[#1A3330] overflow-hidden mr-3" data-id="element-5745">
                              <img src="https://images.unsplash.com/photo-1618761714954-0b8cd0026356?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Product" className="h-full w-full object-cover" data-id="element-5746" />
                            </div>
                            <span className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-5747">
                              Modern UI Kit Pro
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5748">
                          Today, 10:23 AM
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5749">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400" data-id="element-5750">
                            Completed
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] text-right" data-id="element-5751">
                          $49.00
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 dark:hover:bg-[#1A3330]/50 transition-colors" data-id="element-5752">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-5753">
                          #ORD-3F8E1D
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5754">
                          <div className="flex items-center" data-id="element-5755">
                            <div className="h-10 w-10 rounded-md bg-gray-100 dark:bg-[#1A3330] overflow-hidden mr-3" data-id="element-5756">
                              <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Product" className="h-full w-full object-cover" data-id="element-5757" />
                            </div>
                            <span className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-5758">
                              React Dashboard Template
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5759">
                          Yesterday, 4:15 PM
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5760">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400" data-id="element-5761">
                            Completed
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] text-right" data-id="element-5762">
                          $79.00
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 dark:hover:bg-[#1A3330]/50 transition-colors" data-id="element-5763">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-5764">
                          #ORD-9K2M5N
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5765">
                          <div className="flex items-center" data-id="element-5766">
                            <div className="h-10 w-10 rounded-md bg-gray-100 dark:bg-[#1A3330] overflow-hidden mr-3" data-id="element-5767">
                              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Product" className="h-full w-full object-cover" data-id="element-5768" />
                            </div>
                            <span className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-5769">
                              Data Visualization Pack
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5770">
                          Oct 12, 2023
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5771">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400" data-id="element-5772">
                            Processing
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] text-right" data-id="element-5773">
                          $129.00
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 dark:hover:bg-[#1A3330]/50 transition-colors" data-id="element-5774">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-5775">
                          #ORD-1P4L7J
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5776">
                          <div className="flex items-center" data-id="element-5777">
                            <div className="h-10 w-10 rounded-md bg-gray-100 dark:bg-[#1A3330] overflow-hidden mr-3" data-id="element-5778">
                              <img src="https://images.unsplash.com/photo-1618761714954-0b8cd0026356?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80" alt="Product" className="h-full w-full object-cover" data-id="element-5779" />
                            </div>
                            <span className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-5780">
                              Modern UI Kit Pro
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5781">
                          Oct 10, 2023
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5782">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400" data-id="element-5783">
                            Completed
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] text-right" data-id="element-5784">
                          $49.00
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
          {activeTab === 'payouts' && <div className="space-y-6" data-id="element-5785">
              <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330] max-w-2xl mx-auto" data-id="element-5786">
                <div className="w-20 h-20 bg-[#2DB489]/10 dark:bg-[#4ED1A0]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5787">
                  <CreditCardIcon className="h-10 w-10 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5788" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5789">
                  Connect Your Payment Provider
                </h3>
                <p className="text-gray-500 dark:text-[#7A8F8A] mb-8 text-lg" data-id="element-5790">
                  Connect Stripe to receive payouts from your sales. Once
                  connected, earnings will be automatically deposited to your
                  account.
                </p>
                <button className="px-8 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#2DB489]/20 transition-all duration-200 inline-flex items-center" data-id="element-5791">
                  <ExternalLinkIcon className="h-5 w-5 mr-2" data-id="element-5792" />
                  Connect Stripe
                </button>
                <p className="mt-6 text-sm text-gray-400 dark:text-[#7A8F8A]" data-id="element-5793">
                  See documentation for setup instructions
                </p>
              </div>
            </div>}

          {activeTab === 'analytics' && <div className="space-y-6" data-id="element-5794">
              {/* Stats Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5795">
                <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-5796">
                  <div className="flex items-center justify-between mb-4" data-id="element-5797">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-[#7A8F8A]" data-id="element-5798">
                      Total Revenue
                    </h3>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-5799">
                      <DollarSignIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5800" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-5801">
                    $12,450.00
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2 flex items-center" data-id="element-5802">
                    <ArrowRightIcon className="h-3 w-3 mr-1 -rotate-45" data-id="element-5803" />
                    +14.5% from last month
                  </p>
                </div>

                <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-5804">
                  <div className="flex items-center justify-between mb-4" data-id="element-5805">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-[#7A8F8A]" data-id="element-5806">
                      Total Sales
                    </h3>
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-5807">
                      <ShoppingCartIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" data-id="element-5808" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-5809">
                    156
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2 flex items-center" data-id="element-5810">
                    <ArrowRightIcon className="h-3 w-3 mr-1 -rotate-45" data-id="element-5811" />
                    +5.2% from last month
                  </p>
                </div>

                <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-5812">
                  <div className="flex items-center justify-between mb-4" data-id="element-5813">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-[#7A8F8A]" data-id="element-5814">
                      Avg. Order Value
                    </h3>
                    <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg" data-id="element-5815">
                      <BarChart3Icon className="h-5 w-5 text-purple-500 dark:text-purple-400" data-id="element-5816" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-5817">
                    $79.80
                  </p>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-2 flex items-center" data-id="element-5818">
                    <ArrowRightIcon className="h-3 w-3 mr-1" data-id="element-5819" />
                    0.0% from last month
                  </p>
                </div>

                <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-5820">
                  <div className="flex items-center justify-between mb-4" data-id="element-5821">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-[#7A8F8A]" data-id="element-5822">
                      Conversion Rate
                    </h3>
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg" data-id="element-5823">
                      <EyeIcon className="h-5 w-5 text-orange-500 dark:text-orange-400" data-id="element-5824" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-5825">
                    3.2%
                  </p>
                  <p className="text-sm text-red-500 dark:text-red-400 mt-2 flex items-center" data-id="element-5826">
                    <ArrowRightIcon className="h-3 w-3 mr-1 rotate-45" data-id="element-5827" />
                    -1.1% from last month
                  </p>
                </div>
              </div>

              {/* Chart Area */}
              <div className="bg-white dark:bg-[#162825] p-6 rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-5828">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5829">
                  Revenue Overview
                </h3>
                <div className="h-64 flex items-end justify-between gap-2 md:gap-6 pt-4 border-b border-gray-100 dark:border-[#1A3330] pb-2 relative" data-id="element-5830">
                  {/* Y-axis labels */}
                  <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400 dark:text-[#7A8F8A] pb-2" data-id="element-5831">
                    <span data-id="element-5832">$3k</span>
                    <span data-id="element-5833">$2k</span>
                    <span data-id="element-5834">$1k</span>
                    <span data-id="element-5835">$0</span>
                  </div>

                  {/* Bars */}
                  <div className="ml-8 flex-1 flex items-end justify-between gap-2 h-full" data-id="element-5836">
                    <div className="w-full relative group h-full flex items-end" data-id="element-5837">
                      <div className="w-full bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 hover:bg-[#2DB489] dark:hover:bg-[#4ED1A0] rounded-t-sm transition-all duration-300" style={{
                    height: '45%'
                  }} data-id="element-5838"></div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5839">
                        Jul
                      </div>
                    </div>
                    <div className="w-full relative group h-full flex items-end" data-id="element-5840">
                      <div className="w-full bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 hover:bg-[#2DB489] dark:hover:bg-[#4ED1A0] rounded-t-sm transition-all duration-300" style={{
                    height: '60%'
                  }} data-id="element-5841"></div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5842">
                        Aug
                      </div>
                    </div>
                    <div className="w-full relative group h-full flex items-end" data-id="element-5843">
                      <div className="w-full bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 hover:bg-[#2DB489] dark:hover:bg-[#4ED1A0] rounded-t-sm transition-all duration-300" style={{
                    height: '35%'
                  }} data-id="element-5844"></div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5845">
                        Sep
                      </div>
                    </div>
                    <div className="w-full relative group h-full flex items-end" data-id="element-5846">
                      <div className="w-full bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 hover:bg-[#2DB489] dark:hover:bg-[#4ED1A0] rounded-t-sm transition-all duration-300" style={{
                    height: '80%'
                  }} data-id="element-5847"></div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5848">
                        Oct
                      </div>
                    </div>
                    <div className="w-full relative group h-full flex items-end" data-id="element-5849">
                      <div className="w-full bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 hover:bg-[#2DB489] dark:hover:bg-[#4ED1A0] rounded-t-sm transition-all duration-300" style={{
                    height: '65%'
                  }} data-id="element-5850"></div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5851">
                        Nov
                      </div>
                    </div>
                    <div className="w-full relative group h-full flex items-end" data-id="element-5852">
                      <div className="w-full bg-[#2DB489] dark:bg-[#4ED1A0] rounded-t-sm transition-all duration-300 shadow-[0_0_15px_rgba(45,180,137,0.3)]" style={{
                    height: '95%'
                  }} data-id="element-5853"></div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5854">
                        Dec
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'messages' && <div className="space-y-6" data-id="element-5855">
              <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] overflow-hidden flex flex-col md:flex-row h-[600px]" data-id="element-5856">
                {/* Inbox List */}
                <div className="w-full md:w-1/3 border-r border-gray-100 dark:border-[#1A3330] flex flex-col h-full" data-id="element-5857">
                  <div className="p-4 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-5858">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5859">
                      Inbox
                    </h3>
                  </div>
                  <div className="flex-1 overflow-y-auto" data-id="element-5860">
                    {/* Selected Conversation */}
                    <div className="p-4 border-b border-gray-100 dark:border-[#1A3330] bg-gray-50 dark:bg-[#1A3330]/30 cursor-pointer relative" data-id="element-5861">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#2DB489] dark:bg-[#4ED1A0]" data-id="element-5862"></div>
                      <div className="flex items-start gap-3" data-id="element-5863">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold flex-shrink-0" data-id="element-5864">
                          JD
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-5865">
                          <div className="flex justify-between items-baseline mb-1" data-id="element-5866">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] truncate" data-id="element-5867">
                              John Doe
                            </h4>
                            <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5868">
                              10:42 AM
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-[#B0C1BD] truncate" data-id="element-5869">
                            Thanks! The files look great.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Unread Conversation */}
                    <div className="p-4 border-b border-gray-100 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/10 cursor-pointer transition-colors" data-id="element-5870">
                      <div className="flex items-start gap-3" data-id="element-5871">
                        <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold flex-shrink-0" data-id="element-5872">
                          AS
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-5873">
                          <div className="flex justify-between items-baseline mb-1" data-id="element-5874">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] truncate" data-id="element-5875">
                              Alice Smith
                            </h4>
                            <span className="text-xs text-[#2DB489] dark:text-[#4ED1A0] font-medium" data-id="element-5876">
                              Yesterday
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 dark:text-[#F8FAF9] truncate" data-id="element-5877">
                            Question about the UI kit license
                          </p>
                        </div>
                        <div className="w-2.5 h-2.5 rounded-full bg-[#2DB489] dark:bg-[#4ED1A0] mt-1.5 flex-shrink-0" data-id="element-5878"></div>
                      </div>
                    </div>

                    {/* Read Conversation */}
                    <div className="p-4 border-b border-gray-100 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/10 cursor-pointer transition-colors" data-id="element-5879">
                      <div className="flex items-start gap-3" data-id="element-5880">
                        <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold flex-shrink-0" data-id="element-5881">
                          MR
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-5882">
                          <div className="flex justify-between items-baseline mb-1" data-id="element-5883">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] truncate" data-id="element-5884">
                              Mike Ross
                            </h4>
                            <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5885">
                              Oct 12
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-[#7A8F8A] truncate" data-id="element-5886">
                            Do you offer custom modifications?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="hidden md:flex flex-col w-2/3 h-full bg-gray-50/50 dark:bg-[#0F1F1D]/50" data-id="element-5887">
                  <div className="p-4 border-b border-gray-100 dark:border-[#1A3330] bg-white dark:bg-[#162825] flex items-center gap-3" data-id="element-5888">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold" data-id="element-5889">
                      JD
                    </div>
                    <div data-id="element-5890">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9]" data-id="element-5891">
                        John Doe
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-5892">
                        Customer since Oct 2023
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-5893">
                    <div className="flex justify-center" data-id="element-5894">
                      <span className="text-xs text-gray-400 dark:text-[#7A8F8A] bg-gray-100 dark:bg-[#1A3330] px-3 py-1 rounded-full" data-id="element-5895">
                        Today
                      </span>
                    </div>

                    {/* Received Message */}
                    <div className="flex items-start gap-3" data-id="element-5896">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs flex-shrink-0 mt-1" data-id="element-5897">
                        JD
                      </div>
                      <div className="bg-white dark:bg-[#162825] border border-gray-100 dark:border-[#1A3330] p-4 rounded-2xl rounded-tl-sm shadow-sm max-w-[80%]" data-id="element-5898">
                        <p className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-5899">
                          Hi there! I just purchased the Modern UI Kit Pro.
                          Could you tell me if the Figma files include the new
                          dark mode components you mentioned in the update?
                        </p>
                        <span className="text-[10px] text-gray-400 dark:text-[#7A8F8A] mt-2 block" data-id="element-5900">
                          10:15 AM
                        </span>
                      </div>
                    </div>

                    {/* Sent Message */}
                    <div className="flex items-start gap-3 flex-row-reverse" data-id="element-5901">
                      <div className="w-8 h-8 rounded-full bg-[#2DB489] text-white flex items-center justify-center font-bold text-xs flex-shrink-0 mt-1" data-id="element-5902">
                        ME
                      </div>
                      <div className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white p-4 rounded-2xl rounded-tr-sm shadow-sm max-w-[80%]" data-id="element-5903">
                        <p className="text-sm" data-id="element-5904">
                          Hello John! Yes, they absolutely do. You'll find them
                          on the second page of the Figma file labeled "Dark
                          Theme Components". Let me know if you have any trouble
                          finding them!
                        </p>
                        <span className="text-[10px] text-[#162825]/70 mt-2 block text-right" data-id="element-5905">
                          10:30 AM
                        </span>
                      </div>
                    </div>

                    {/* Received Message */}
                    <div className="flex items-start gap-3" data-id="element-5906">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs flex-shrink-0 mt-1" data-id="element-5907">
                        JD
                      </div>
                      <div className="bg-white dark:bg-[#162825] border border-gray-100 dark:border-[#1A3330] p-4 rounded-2xl rounded-tl-sm shadow-sm max-w-[80%]" data-id="element-5908">
                        <p className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-5909">
                          Found them! Thanks! The files look great.
                        </p>
                        <span className="text-[10px] text-gray-400 dark:text-[#7A8F8A] mt-2 block" data-id="element-5910">
                          10:42 AM
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-white dark:bg-[#162825] border-t border-gray-100 dark:border-[#1A3330]" data-id="element-5911">
                    <div className="relative" data-id="element-5912">
                      <input type="text" placeholder="Type a message..." className="w-full bg-gray-50 dark:bg-[#0F1F1D] border border-gray-200 dark:border-[#1A3330] rounded-full py-3 pl-4 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489]/50 dark:text-white" disabled data-id="element-5913" />
                      <button className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-full hover:bg-[#25A077] transition-colors" disabled data-id="element-5914">
                        <ArrowRightIcon className="h-4 w-4" data-id="element-5915" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </main>
      <Footer data-id="element-5916" />
      {/* Add the delete confirmation modal */}
      <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={cancelDelete} onConfirm={confirmDelete} isLoading={isDeleting} title="Delete Product Permanently" message="Are you sure you want to permanently delete this product? This action cannot be undone and will remove all associated files. Purchase records will be preserved but marked as referencing a deleted product." data-id="element-5917" />
    </div>;
};